import { getSessionActivity, type SessionActivitySource } from '../lib/normalized-session-presenter';
import type { ConnectionState } from '../types';
import type { DshSessionView } from '../stores/useDshSessionStore';
import { usePresenceStore } from '../stores/usePresenceStore';

export interface DshSessionConnection {
  connected: boolean;
  canControlLive: boolean;
  status: 'connecting' | 'reconnecting' | 'disconnected' | 'waiting' | 'working' | 'idle' | 'failed' | 'closed' | 'awaiting-session' | 'session-unavailable' | 'recovery-failed' | 'observation-stopped';
  indicator: 'connecting' | 'disconnected' | 'waiting' | 'working' | 'idle';
}

/** Browser connectivity and received session activity do not assert agent presence. */
export function dshSessionConnection(connection: ConnectionState, session?: SessionActivitySource): DshSessionConnection {
  if (connection !== 'connected') return {
    connected: false,
    canControlLive: false,
    status: connection === 'connecting' ? 'connecting' : connection === 'reconnecting' ? 'reconnecting' : 'disconnected',
    indicator: connection === 'disconnected' ? 'disconnected' : 'connecting',
  };
  const { status } = getSessionActivity(session);
  if (status === 'failed' || status === 'closed') return {
    connected: true, canControlLive: false, status, indicator: 'idle',
  };
  const activity = status === 'running' || status === 'starting' ? 'working' : status;
  return {
    connected: true,
    canControlLive: true,
    status: activity,
    indicator: activity,
  };
}

export function useDshSessionConnection(session?: Pick<DshSessionView, 'active' | 'pending' | 'runtimeInfo' | 'archivedInteractions' | 'nativeCapabilitiesLive' | 'recoveryStatus' | 'servicePaused' | 'observationAvailability'>): DshSessionConnection {
  const connection = usePresenceStore(state => state.connectionState);
  const state = dshSessionConnection(connection, session);
  if (state.connected && session?.observationAvailability?.status === 'stopped') return { ...state, status: 'observation-stopped', indicator: 'disconnected', canControlLive: false };
  if (session?.servicePaused) return { ...state, canControlLive: false };
  if (state.status === 'idle' && session?.archivedInteractions.length && session.nativeCapabilitiesLive !== true) {
    return { ...state, status: session.recoveryStatus === 'failed' ? 'recovery-failed' : session.recoveryStatus === 'unavailable' ? 'session-unavailable' : 'awaiting-session', indicator: 'disconnected' };
  }
  return state;
}
